import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store';
import { setReimbursementRef } from '../store/slices/paymentSlice';
import { getReimbursements } from '../api/reimbursement';

interface UseAuthGuardReturn {
  isChecking: boolean;
  isAuthorized: boolean;
}

export const useAuthGuard = (): UseAuthGuardReturn => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const reduxRef = useAppSelector((state) => state.payment.reimbursementRef);
  const urlRef = new URLSearchParams(search).get('ref');
  const storedRef = sessionStorage.getItem('ref');

  const ref = urlRef || reduxRef || storedRef;

  const [isChecking, setIsChecking] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    if (ref) {
      sessionStorage.setItem('ref', ref);
      dispatch(setReimbursementRef(ref));
    }
  }, [ref, dispatch]);

  useEffect(() => {
    const checkAuth = async () => {
      const homePath = '/';
      const thankYouPath = '/thankYou';

      // If no ref and not on home page, redirect to home
      if (!ref) {
        if (pathname !== homePath) {
          navigate(homePath, { replace: true });
        }
        setIsChecking(false);
        setIsAuthorized(pathname === homePath);
        return;
      }

      try {
        const data = await getReimbursements(ref);
        const isSubmitted = Boolean(data?.submittedDateTime);

        // If already submitted and trying to access quiz/summary pages, redirect to home
        // But allow access to thankYou page
        if (isSubmitted && pathname !== homePath && pathname !== thankYouPath) {
          navigate(homePath, { replace: true });
          setIsAuthorized(false);
        } else {
          setIsAuthorized(true);
        }
      } catch (error) {
        // If API fails and on thankYou page, still allow access (form might have just been submitted)
        if (pathname === thankYouPath) {
          setIsAuthorized(true);
        } else {
          // Otherwise redirect to home
          navigate(homePath, { replace: true });
          setIsAuthorized(false);
        }
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, [ref, pathname, navigate]);

  return { isChecking, isAuthorized };
};

export default useAuthGuard;
